import { faBook } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import ToastContext from '../../../context/ToastContext';
import { FileTypes } from '../../../enums/files';
import useAxiosHook from '../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../hooks/useEndpointGuard';
import usePageTitle from '../../../hooks/usePageTitle';
import useRouteDialog from '../../../hooks/useRouteDialog';
import useTableColumns from '../../../hooks/useTableColumns';
import useTableState from '../../../hooks/useTableState';
import useToastMessage from '../../../hooks/useToastMessage';
import {
  PriceListCollection,
  PriceListParentsCollection,
} from '../../../types/api/priceLists';
import { LabelValue } from '../../../types/options';
import {
  createGuard,
  deleteGuard,
  editGuard,
} from '../../../utils/constants/auth/priceLists';
import { routeGuard as pricesPageGuard } from '../../../utils/constants/auth/prices';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../utils/constants/routePaths';
import { downloadFile, getFileName } from '../../../utils/helpers/files';
import { sequential } from '../../../utils/helpers/functions';
import { queryString } from '../../../utils/helpers/http';
import Table from '../../DataTable/Table/Table';
import Flex from '../../layout/flex/Flex';
import MainContent from '../../layout/flex/MainContent';
import Sidebar from '../../layout/flex/Sidebar';
import Filters from '../Components/Filters/Filters';
import HeaderPages from '../Components/HeaderPages/HeaderPages';
import CreateEditDialog from './Dialogs/CreateEdit/CreateEditDialog';
import DeleteDialog from './Dialogs/Delete/DeleteDialog';
import ViewDialog from './Dialogs/View/ViewDialog';
import {
  Row,
  additionalColumnProperties,
  getColumnHeadersMap,
  tableStorageKey,
} from './PriceLists.functions';
import useTableFilters from './useTableFilters';

function PriceLists(): JSX.Element {
  const { t } = useTranslation();
  usePageTitle(t('Price Lists'));
  const { bottomRightToastRef } = useContext(ToastContext);
  const history = useHistory();
  const [action, setAction] = useState<string>('');
  const createEpGuard = useEndpointGuard(createGuard);
  const editEpGuard = useEndpointGuard(editGuard);
  const deleteEpGuard = useEndpointGuard(deleteGuard);
  const pricesPageEpGuard = useEndpointGuard(pricesPageGuard);

  const { data: priceListParents, error: priceListParentsError } =
    useAxiosHook<PriceListParentsCollection>('/price-lists/parents');

  useToastMessage(undefined, priceListParentsError, {
    error: {
      summary: t('An error occured while reading base price list data.'),
    },
  });

  const parentOptions = useMemo<LabelValue[]>(
    () =>
      priceListParents?.map((p) => ({ label: p.name, value: String(p.id) })) ??
      [],
    [priceListParents]
  );
  const createEditParentOptions = useMemo<LabelValue[]>(
    () => [
      { label: t('This price list is a base price list'), value: '-1' },
      ...parentOptions,
    ],
    [parentOptions, t]
  );

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    sortOrder,
    selection,
    setSortField,
    setSortOrder,
    setSelection,
    contextMenuSelection,
    setContextMenuSelection,
  } = useTableState<Row>(tableStorageKey);

  const { filters, headerFiltersCount, resetAllFilters, httpFiltersObj } =
    useTableFilters(page, setPage, limit, parentOptions);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const { selectedColumns, setSelectedColumns, columnOptions, columns } =
    useTableColumns(
      page,
      limit,
      'priceLists',
      columnHeadersMap,
      columnHeadersMap,
      (c) =>
        additionalColumnProperties(
          t,
          c as keyof typeof columnHeadersMap,
          setContextMenuSelection,
          setAction,
          editEpGuard,
          pricesPageEpGuard,
          deleteEpGuard
        )
    );

  const finalColumns = useMemo<JSX.Element[]>(
    () => [
      ...columns,
      <Column
        key="action-column"
        header={t('Actions')}
        field="actions"
        frozen
        alignFrozen="right"
        {...additionalColumnProperties(
          t,
          'actions',
          setContextMenuSelection,
          setAction,
          editEpGuard,
          deleteEpGuard,
          deleteEpGuard
        )}
      />,
    ],
    [columns, t, setContextMenuSelection, setAction, editEpGuard, deleteEpGuard]
  );

  const {
    show: showViewDialog,
    hide: hideViewDialog,
    isVisible: isViewDialogVisible,
  } = useRouteDialog(
    RoutePaths.PriceLists,
    constructIdRoute(RoutePaths.ViewPriceList, contextMenuSelection?.id),
    true
  );

  const {
    show: showCreateDialog,
    hide: hideCreateDialog,
    isVisible: isCreateDialogVisible,
  } = useRouteDialog(
    RoutePaths.PriceLists,
    RoutePaths.AddPriceList,
    createEpGuard
  );

  const {
    show: showEditDialog,
    hide: hideEditDialog,
    isVisible: isEditDialogVisible,
  } = useRouteDialog(
    RoutePaths.PriceLists,
    constructIdRoute(RoutePaths.EditPriceList, contextMenuSelection?.id),
    editEpGuard
  );
  const {
    show: showDeleteDialog,
    hide: hideDeleteDialog,
    isVisible: isDeleteDialogVisible,
  } = useRouteDialog(
    RoutePaths.PriceLists,
    constructIdRoute(RoutePaths.DeletePriceList, contextMenuSelection?.id),
    deleteEpGuard
  );

  const { data, error, isLoading, reload } = useAxiosHook<PriceListCollection>(
    '/price-lists' + queryString(httpFiltersObj)
  );

  useToastMessage(undefined, error, {
    error: {
      summary: t('An error occured while reading price list data.'),
    },
  });

  const handleCmPriceDefinitionClick = useCallback(() => {
    if (!contextMenuSelection) {
      return;
    }

    history.push(
      '/prices' + queryString({ priceList: contextMenuSelection?.id })
    );
  }, [history, contextMenuSelection]);

  const handleCmPrintPricesClick = useCallback(() => {
    if (!contextMenuSelection) {
      return;
    }

    downloadFile(
      `/replace/me`, // !!!
      getFileName(t('PriceLists'), contextMenuSelection.name ?? ''),
      FileTypes.PDF,
      bottomRightToastRef
    );
  }, [bottomRightToastRef, contextMenuSelection, t]);

  useEffect(() => {
    if (action && contextMenuSelection) {
      if (action === 'view-details') {
        showViewDialog();
      }
      if (action === 'edit') {
        showEditDialog();
      }
      if (action === 'price-definitions') {
        handleCmPriceDefinitionClick();
      }
      if (action === 'print-price') {
        handleCmPrintPricesClick();
      }
      if (action === 'delete') {
        showDeleteDialog();
      }

      setAction('');
    }
  }, [
    action,
    contextMenuSelection,
    handleCmPriceDefinitionClick,
    handleCmPrintPricesClick,
    showViewDialog,
    showEditDialog,
    showDeleteDialog,
    setAction,
  ]);

  function handleCreateLogisticsRoute() {
    setContextMenuSelection(undefined);
    showCreateDialog();
  }

  function handelEditButton(data: any) {
    setContextMenuSelection(data);
    showEditDialog();
  }

  return (
    <div className="page price-lists">
      <HeaderPages
        title={t('Price Lists')}
        subtitle={t('View and manage price lists')}
        icon={faBook}
      >
        <Button
          type="button"
          label={t('Create a price list')}
          icon="fas fa-plus"
          onClick={handleCreateLogisticsRoute}
          className="main-btn"
        />
      </HeaderPages>

      <ViewDialog
        isShown={isViewDialogVisible}
        onHide={hideViewDialog}
        isEditBtnShown
        setIsEditDialog={handelEditButton}
      />

      <CreateEditDialog
        isEditDialog={isEditDialogVisible}
        isShown={isCreateDialogVisible || isEditDialogVisible}
        onHide={() => sequential(hideEditDialog, hideCreateDialog)}
        parentOptions={createEditParentOptions}
        reloadCollection={reload}
      />

      <DeleteDialog
        isShown={isDeleteDialogVisible}
        onHide={hideDeleteDialog}
        reloadCollection={reload}
      />

      <Flex direction="column">
        <Sidebar>
          <Filters
            filters={filters}
            resetAllFilters={resetAllFilters}
            headerFiltersCount={headerFiltersCount}
            filterHeight={180}
          />
        </Sidebar>
        <MainContent>
          <Table
            tableRef={tableRef}
            columns={finalColumns}
            data={data}
            isLoading={isLoading}
            hasError={!!error}
            reload={reload}
            headerTitle=""
            filterHeight={180}
            headerFiltersCount={headerFiltersCount}
            onHeaderFiltersResetAllBtnClick={resetAllFilters}
            setPage={setPage}
            setLimit={setLimit}
            sortField={sortField}
            rows={limit}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setSelection={setSelection}
            sortOrder={sortOrder}
            selection={selection}
            storageString={tableStorageKey}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            columnOptions={columnOptions}
            contextMenuSelection={contextMenuSelection}
            setContextMenuSelection={setContextMenuSelection}
            exportToExcelButton
          />
        </MainContent>
      </Flex>
    </div>
  );
}

export default PriceLists;
